// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference, normalizeErrorString } from '@medplum/core';
import type { Patient, Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { Document, Loading, QuestionnaireForm, useMedplum, useMedplumProfile } from '@medplum/react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { botIdentifier } from '../lib/bot-identifier';
import { withTenantScope } from '../utils/tenant';

export function ConsentPage(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient;
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    medplum
      .searchOne('Questionnaire', { name: 'patient-consent-form' })
      .then((result) => {
        setQuestionnaire(result);
      })
      .catch((error) => {
        showNotification({
          color: 'red',
          message: normalizeErrorString(error),
          autoClose: false,
        });
      })
      .finally(() => setLoading(false));
  }, [medplum]);

  const handleOnSubmit = useCallback(
    async (response: QuestionnaireResponse) => {
      if (!questionnaire || !profile) {
        return;
      }

      setSubmitting(true);
      try {
        // Add the patient reference and tenant scope to the response
        const responseWithSubject: QuestionnaireResponse = withTenantScope({
          ...response,
          subject: createReference(profile),
        });

        await medplum.executeBot(botIdentifier('process-patient-consent'), responseWithSubject);

        showNotification({
          color: 'green',
          message: 'Consent submitted successfully',
        });

        // Force page reload to refresh the cached profile (tag has been removed)
        window.location.href = '/';
      } catch (error) {
        showNotification({
          color: 'red',
          message: normalizeErrorString(error),
          autoClose: false,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [medplum, profile, questionnaire]
  );

  if (loading) {
    return <Loading />;
  }

  if (!questionnaire) {
    return (
      <Document width={800}>
        <Alert color="red" title="Questionnaire not found">
          The consent form questionnaire could not be found. Please contact support.
        </Alert>
      </Document>
    );
  }

  return (
    <Document width={800}>
      <Title order={2} mb="md">
        Complete Your Consent
      </Title>
      <Alert color="blue" mb="md">
        Before you can access your patient portal, please review and agree to the following consent forms.
      </Alert>
      <QuestionnaireForm
        questionnaire={questionnaire}
        onSubmit={handleOnSubmit}
        submitButtonText={submitting ? 'Submitting...' : 'Submit Consent'}
      />
    </Document>
  );
}
